import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  Award,
  CheckCircle,
  TrendingUp,
  AlertTriangle,
  MessageSquare,
  BookOpen,
  ArrowRight,
  Sparkles,
  Lightbulb,
  GraduationCap,
  Clock
} from 'lucide-react'
import { ReviewSynthesisReport } from './ReviewSynthesisReport'
import { DiscussionPointsSummary } from './DiscussionPointsSummary'
import { RecordedByHrNote, type RecordedByHr } from './RecordedByHrNote'
import type { DiscussionPoint } from '@/services/api/reviewService'
import type { ReviewSynthesis } from '@/services/aiService'
import type { KPAData } from '@/types/evidence'
import type { IndividualDevelopmentPlan } from '@/services/api/idpService'
import { growthPlanName, type GrowthPlanViewer } from '@/lib/growthPlanWording'

interface ReviewFinalizeViewProps {
  /** Who is looking: the People Leader reads "Growth Plan"; the Team Member, once the
   *  check-in is completed, reads "My Personalised Growth" (HR UAT list #3). */
  viewerRole: GrowthPlanViewer
  memberName: string
  isFormalReview: boolean
  isReviewCompleted: boolean
  canRate: boolean
  /** Too Soon To Rate (HR UAT list #20): only TSTR can be chosen. */
  tooSoonToRate?: { first_hire: string } | null
  /** HR recorded the check-in (#24) or its calibration (#26) on someone's behalf. A completed
   *  check-in opens here, so the summary says so first. */
  recordedByHr: RecordedByHr | null
  calibrationRecordedByHr: boolean
  allKPAsRated: boolean
  kpaData: KPAData[]
  kpaRatings: Record<string, 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr'>
  overallRating: 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr' | null
  setOverallRating: (rating: 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr') => void
  ratingJustification: string
  setRatingJustification: (val: string) => void
  reviewSynthesis: ReviewSynthesis | null
  loadingSynthesis: boolean
  onGenerateSynthesis: () => void
  onExportPDF: () => void
  onDeleteSynthesis?: () => void
  onSaveSynthesis?: (edited: any) => void
  kpaDiscussionPoints: Record<string, { wentWell: DiscussionPoint[], challenges: DiscussionPoint[], actions: DiscussionPoint[] }>
  /** The KPAs as discussed (a completed check-in's snapshot), naming its discussion points; else kpaData. */
  discussedKpaData?: KPAData[]
  memberIDP: IndividualDevelopmentPlan | null
  loadingIDP: boolean
  prepareIDPTransferItems: () => void
  showIDPTransfer: boolean
  setShowIDPTransfer: (val: boolean) => void
  idpTransferItems: {
    strengths: { text: string; kpaId: string; selected: boolean }[]
    developmentAreas: { text: string; kpaId: string; selected: boolean }[]
    interventions: { text: string; kpaId: string; selected: boolean }[]
  }
  transferringToIDP: boolean
  toggleIDPTransferItem: (type: 'strengths' | 'developmentAreas' | 'interventions', index: number) => void
  handleTransferToIDP: () => void
  onCompleteReview: () => void
  onConfirmCompleteOpen: () => void
  setViewMode: (mode: 'kpa' | 'feedback' | 'finalize') => void
  getReviewTypeLabel: () => string
}

const getRatingColor = (rating: string | null) => {
  switch (rating) {
    case 'exceptional': return 'bg-purple-100 text-purple-800 border-purple-200'
    case 'full': return 'bg-green-100 text-green-800 border-green-200'
    case 'not-yet-full': return 'bg-amber-100 text-amber-800 border-amber-200'
    case 'weak': return 'bg-red-100 text-red-800 border-red-200'
    case 'tstr': return 'bg-slate-100 text-slate-700 border-slate-300'
    default: return 'bg-gray-100 text-gray-800 border-gray-200'
  }
}

const getRatingLabel = (rating: string | null) => {
  if (!rating) return 'Not Rated'
  if (rating === 'tstr') return 'Too Soon to Rate'
  return rating.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
}

export function ReviewFinalizeView({
  viewerRole,
  memberName,
  isFormalReview,
  isReviewCompleted,
  canRate,
  tooSoonToRate,
  recordedByHr,
  calibrationRecordedByHr,
  allKPAsRated,
  kpaData,
  kpaRatings,
  overallRating,
  setOverallRating,
  ratingJustification,
  setRatingJustification,
  reviewSynthesis,
  loadingSynthesis,
  onGenerateSynthesis,
  onExportPDF,
  onDeleteSynthesis,
  onSaveSynthesis,
  kpaDiscussionPoints,
  discussedKpaData,
  memberIDP,
  loadingIDP,
  prepareIDPTransferItems,
  showIDPTransfer,
  setShowIDPTransfer,
  idpTransferItems,
  transferringToIDP,
  toggleIDPTransferItem,
  handleTransferToIDP,
  onCompleteReview,
  onConfirmCompleteOpen,
  setViewMode,
  getReviewTypeLabel,
}: ReviewFinalizeViewProps) {
  const isLeader = viewerRole === 'manager'
  const planName = growthPlanName(viewerRole)
  // Whose plan it is, as the viewer says it.
  const theirPlan = isLeader ? `${memberName}'s Growth Plan` : planName
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
        <RecordedByHrNote recordedByHr={recordedByHr} calibrationRecordedByHr={calibrationRecordedByHr} />
        {/* Overall Rating Section (Formal Reviews Only) */}
        {isFormalReview && <Card>
          <CardHeader className="bg-gradient-to-br from-heritage-blue to-future-blue text-white">
            <CardTitle className="flex items-center gap-2">
              <Award className="h-6 w-6" />
              Overall Impact & Growth Indicator
            </CardTitle>
            <p className="text-blue-100 text-sm">
              Based on all KPA discussions and indicators, provide an overall impact & growth indicator for {memberName}
            </p>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* KPA Ratings Summary */}
            <div>
              <h3 className="font-semibold mb-3">KPA Impact & Growth Indicators Summary</h3>
              <div className="grid grid-cols-2 gap-3">
                {kpaData.map((kpa) => (
                  <div key={kpa.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium truncate">{kpa.display_name || kpa.name}</span>
                    <Badge className={`${getRatingColor(kpaRatings[kpa.id])} border`}>
                      {getRatingLabel(kpaRatings[kpa.id])}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>

            {/* Overall Rating Selection */}
            <div>
              {!isReviewCompleted && <h3 className="font-semibold text-lg mb-4">Select Overall Impact & Growth Indicator</h3>}
              {overallRating && (
                <div className="mb-4 p-4 bg-white rounded-xl border-2 border-indigo-300 shadow-sm">
                  <p className="text-sm font-medium text-gray-700 mb-2">Current Overall Impact & Growth Indicator:</p>
                  <Badge className={`${getRatingColor(overallRating)} border-2 text-lg px-4 py-1`}>
                    {getRatingLabel(overallRating)}
                  </Badge>
                </div>
              )}

              {/* Rating selection — only when the review is still being rated.
                  A completed review shows the locked-in rating above (read-only),
                  not the interactive option cards. */}
              {!isReviewCompleted && tooSoonToRate && (
                <p className="rounded-lg border border-slate-300 bg-slate-50 p-3 text-sm text-slate-700">
                  {memberName.split(' ')[0]} started on {new Date(`${tooSoonToRate.first_hire}T00:00:00`).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })},
                  less than three months before the check-ins end, so the overall rating is Too Soon To Rate.
                </p>
              )}
              {!isReviewCompleted && (
              <div className="grid grid-cols-2 gap-4">
                {!tooSoonToRate && (<>
                <button
                  type="button"
                  className={`relative h-auto p-6 rounded-xl border-2 transition-all duration-200 text-left ${
                    overallRating === 'exceptional'
                      ? 'bg-gradient-to-br from-purple-500 to-purple-600 border-purple-600 shadow-xl transform scale-[1.02]'
                      : 'bg-white border-purple-200 hover:border-purple-400 hover:shadow-lg hover:scale-[1.01]'
                  }`}
                  onClick={() => !isReviewCompleted && setOverallRating('exceptional')}
                  disabled={isReviewCompleted}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Award className={`h-5 w-5 ${overallRating === 'exceptional' ? 'text-white' : 'text-purple-600'}`} />
                    <span className={`font-bold text-lg ${overallRating === 'exceptional' ? 'text-white' : 'text-purple-900'}`}>
                      Exceptional
                    </span>
                  </div>
                  <p className={`text-sm leading-relaxed whitespace-normal break-words ${
                    overallRating === 'exceptional' ? 'text-white/95' : 'text-gray-700'
                  }`}>
                    Delivers significantly more work than required - much bigger than their jobs. Significantly exceeding challenging goals and targets on all KPAs
                  </p>
                </button>

                <button
                  type="button"
                  className={`relative h-auto p-6 rounded-xl border-2 transition-all duration-200 text-left ${
                    overallRating === 'full'
                      ? 'bg-gradient-to-br from-green-500 to-green-600 border-green-600 shadow-xl transform scale-[1.02]'
                      : 'bg-white border-green-200 hover:border-green-400 hover:shadow-lg hover:scale-[1.01]'
                  }`}
                  onClick={() => !isReviewCompleted && setOverallRating('full')}
                  disabled={isReviewCompleted}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className={`h-5 w-5 ${overallRating === 'full' ? 'text-white' : 'text-green-600'}`} />
                    <span className={`font-bold text-lg ${overallRating === 'full' ? 'text-white' : 'text-green-900'}`}>
                      Full
                    </span>
                  </div>
                  <p className={`text-sm leading-relaxed whitespace-normal break-words ${
                    overallRating === 'full' ? 'text-white/95' : 'text-gray-700'
                  }`}>
                    Gets all the work done. Consistently achieving all mission critical targets and standards over all KPAs
                  </p>
                </button>

                <button
                  type="button"
                  className={`relative h-auto p-6 rounded-xl border-2 transition-all duration-200 text-left ${
                    overallRating === 'not-yet-full'
                      ? 'bg-gradient-to-br from-amber-500 to-amber-600 border-amber-600 shadow-xl transform scale-[1.02]'
                      : 'bg-white border-amber-200 hover:border-amber-400 hover:shadow-lg hover:scale-[1.01]'
                  }`}
                  onClick={() => !isReviewCompleted && setOverallRating('not-yet-full')}
                  disabled={isReviewCompleted}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className={`h-5 w-5 ${overallRating === 'not-yet-full' ? 'text-white' : 'text-amber-600'}`} />
                    <span className={`font-bold text-lg ${overallRating === 'not-yet-full' ? 'text-white' : 'text-amber-900'}`}>
                      Not Yet Full
                    </span>
                  </div>
                  <p className={`text-sm leading-relaxed whitespace-normal break-words ${
                    overallRating === 'not-yet-full' ? 'text-white/95' : 'text-gray-700'
                  }`}>
                    Does not yet get all the work done – however, real improvement is evident all around (may include exceptional performers in new challenging positions)
                  </p>
                </button>

                <button
                  type="button"
                  className={`relative h-auto p-6 rounded-xl border-2 transition-all duration-200 text-left ${
                    overallRating === 'weak'
                      ? 'bg-gradient-to-br from-red-500 to-red-600 border-red-600 shadow-xl transform scale-[1.02]'
                      : 'bg-white border-red-200 hover:border-red-400 hover:shadow-lg hover:scale-[1.01]'
                  }`}
                  onClick={() => !isReviewCompleted && setOverallRating('weak')}
                  disabled={isReviewCompleted}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className={`h-5 w-5 ${overallRating === 'weak' ? 'text-white' : 'text-red-600'}`} />
                    <span className={`font-bold text-lg ${overallRating === 'weak' ? 'text-white' : 'text-red-900'}`}>
                      Weak
                    </span>
                  </div>
                  <p className={`text-sm leading-relaxed whitespace-normal break-words ${
                    overallRating === 'weak' ? 'text-white/95' : 'text-gray-700'
                  }`}>
                    Unable to get the work done - not showing signs of real improvement. Immediate and significant improvement in impact & growth is needed
                  </p>
                </button>
                </>)}

                {/* TSTR — 5th category for employees with under 3 months
                    Eduvos tenure. Spans the grid row; excluded from
                    distributions/averages/9-box downstream. */}
                <button
                  type="button"
                  className={`relative h-auto p-6 rounded-xl border-2 transition-all duration-200 text-left col-span-2 ${
                    overallRating === 'tstr'
                      ? 'bg-gradient-to-br from-slate-500 to-slate-600 border-slate-600 shadow-xl transform scale-[1.01]'
                      : 'bg-white border-slate-200 hover:border-slate-400 hover:shadow-lg hover:scale-[1.005]'
                  }`}
                  onClick={() => !isReviewCompleted && setOverallRating('tstr')}
                  disabled={isReviewCompleted}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className={`h-5 w-5 ${overallRating === 'tstr' ? 'text-white' : 'text-slate-600'}`} />
                    <span className={`font-bold text-lg ${overallRating === 'tstr' ? 'text-white' : 'text-slate-900'}`}>
                      Too Soon to Rate (TSTR)
                    </span>
                  </div>
                  <p className={`text-sm leading-relaxed whitespace-normal break-words ${
                    overallRating === 'tstr' ? 'text-white/95' : 'text-gray-700'
                  }`}>
                    Team member has been with Eduvos for less than 3 months — not enough tenure for a meaningful impact & growth assessment. Excluded from indicator distributions and talent analytics.
                  </p>
                </button>
              </div>
              )}
            </div>

            {/* Rating Justification */}
            {overallRating && (
              <div className="mt-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Impact & Growth Indicator Justification <span className="text-red-500">*</span>
                </label>
                <p className="text-sm text-gray-600 mb-3">
                  Provide a clear explanation for the overall indicator. This will be shared with the team member and used during calibration.
                </p>
                <textarea
                  value={ratingJustification}
                  onChange={(e) => setRatingJustification(e.target.value)}
                  className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-heritage-blue focus:border-transparent resize-none"
                  placeholder="Explain the reasoning behind this indicator, highlighting key strengths, areas for development, and how the indicator reflects impact & growth across all KPAs..."
                  required
                  disabled={isReviewCompleted}
                  readOnly={isReviewCompleted}
                />
                {ratingJustification.length > 0 && (
                  <p className="text-sm text-gray-500 mt-1">
                    {ratingJustification.length} characters
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>}

        {/* Overall Comments Section (Informal Check-ins) */}
        {!isFormalReview && (
          <Card>
            <CardHeader className="bg-gradient-to-br from-heritage-blue to-future-blue text-white">
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-6 w-6" />
                Overall Comments
              </CardTitle>
              <p className="text-blue-100 text-sm">
                Summarise the key themes and outcomes from this check-in with {memberName}
              </p>
            </CardHeader>
            <CardContent className="p-6">
              <textarea
                value={ratingJustification}
                onChange={(e) => setRatingJustification(e.target.value)}
                className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-heritage-blue focus:border-transparent resize-none"
                placeholder="Capture overall observations, agreed actions, and key takeaways from the check-in discussion..."
                disabled={isReviewCompleted}
                readOnly={isReviewCompleted}
              />
              {ratingJustification.length > 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  {ratingJustification.length} characters
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* A completed check-in's summary lists its discussion points (HR UAT list #4) */}
        {isReviewCompleted && (
          <DiscussionPointsSummary kpaData={discussedKpaData ?? kpaData} points={kpaDiscussionPoints} />
        )}

        {/* Check-in / Review Synthesis (Inline, Collapsible) */}
        {(overallRating || !isFormalReview) && (
          <ReviewSynthesisReport
            viewerRole={viewerRole}
            synthesis={reviewSynthesis}
            isLoading={loadingSynthesis}
            onGenerateSynthesis={onGenerateSynthesis}
            onExportPDF={onExportPDF}
            onDeleteSynthesis={onDeleteSynthesis}
            onSaveSynthesis={onSaveSynthesis}
            isFormalReview={isFormalReview}
            isReadOnly={isReviewCompleted}
          />
        )}

        {/* Growth Plan Integration Section — named by who is looking */}
        <Card className="border-2 border-dashed border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <BookOpen className="h-5 w-5 text-blue-600" />
              {planName}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* IDP Status */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {loadingIDP ? (
                    <div className="flex items-center gap-2 text-gray-500">
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                      <span className="text-sm">Checking {isLeader ? 'their Growth Plan' : planName}...</span>
                    </div>
                  ) : memberIDP ? (
                    <div className="flex items-center gap-2">
                      <Badge className="bg-green-100 text-green-700 border-green-200">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        {planName} Active
                      </Badge>
                      <span className="text-sm text-gray-600">
                        {memberIDP.strengths?.length || 0} strengths, {memberIDP.development_areas?.length || 0} development areas, {memberIDP.interventions?.length || 0} interventions
                      </span>
                    </div>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      No {planName} Initiated
                    </Badge>
                  )}
                </div>
              </div>

              {/* Description */}
              <p className="text-sm text-gray-600">
                Transfer key discussion outcomes from this check-in to {theirPlan}.
                Strengths, development areas, and action items can be added to it directly.
              </p>

              {/* Summary of transferable items */}
              {(Object.keys(kpaDiscussionPoints).length > 0 || reviewSynthesis) && (
                <div className="bg-white rounded-lg p-3 border border-blue-100">
                  <p className="text-xs font-medium text-gray-700 mb-2">Available to transfer:</p>
                  <div className="flex flex-wrap gap-2">
                    {(() => {
                      const wentWellCount = Object.values(kpaDiscussionPoints).reduce((sum, points) => sum + (points.wentWell?.length || 0), 0)
                      const challengesCount = Object.values(kpaDiscussionPoints).reduce((sum, points) => sum + (points.challenges?.length || 0), 0)
                      const actionsCount = Object.values(kpaDiscussionPoints).reduce((sum, points) => sum + (points.actions?.length || 0), 0)

                      return (
                        <>
                          {wentWellCount > 0 && (
                            <Badge variant="outline" className="bg-green-50 text-green-700">
                              <Sparkles className="h-3 w-3 mr-1" />
                              {wentWellCount} Strengths
                            </Badge>
                          )}
                          {challengesCount > 0 && (
                            <Badge variant="outline" className="bg-amber-50 text-amber-700">
                              <Lightbulb className="h-3 w-3 mr-1" />
                              {challengesCount} Development Areas
                            </Badge>
                          )}
                          {actionsCount > 0 && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700">
                              <GraduationCap className="h-3 w-3 mr-1" />
                              {actionsCount} Interventions
                            </Badge>
                          )}
                          {wentWellCount === 0 && challengesCount === 0 && actionsCount === 0 && (
                            <span className="text-xs text-gray-500">No discussion points recorded yet</span>
                          )}
                        </>
                      )
                    })()}
                  </div>
                </div>
              )}

              {/* Action Button */}
              <Button
                onClick={prepareIDPTransferItems}
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={Object.keys(kpaDiscussionPoints).length === 0 && !reviewSynthesis}
              >
                <ArrowRight className="h-4 w-4 mr-2" />
                {memberIDP ? `Transfer to ${planName}` : `Initiate ${planName} & Transfer`}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Finalise Footer Actions */}
      <div className="border-t bg-gray-50 p-4 flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => setViewMode('kpa')}
        >
          Back to KPA Check-ins
        </Button>

        {!isReviewCompleted && isFormalReview && (
          <Button
            className="bg-green-600 hover:bg-green-700"
            disabled={!overallRating || !ratingJustification.trim() || !allKPAsRated}
            onClick={onCompleteReview}
            data-tour="review-complete-btn"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            {!allKPAsRated ? 'Rate All KPAs First' : !overallRating ? 'Select Overall Indicator' : !ratingJustification.trim() ? 'Add Justification' : 'Complete Check-In & Submit'}
          </Button>
        )}
        {!isReviewCompleted && !isFormalReview && (
          <Button
            className="bg-green-600 hover:bg-green-700"
            onClick={onConfirmCompleteOpen}
            data-tour="review-complete-btn"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Complete Check-in & Submit
          </Button>
        )}
      </div>

      {/* IDP Transfer Modal */}
      <Dialog open={showIDPTransfer} onOpenChange={setShowIDPTransfer}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-blue-600" />
              Transfer to {theirPlan}
            </DialogTitle>
            <DialogDescription>
              Select items from the check-in discussion to add to {isLeader ? 'their Growth Plan' : planName}.
              {!memberIDP && ` This will also initiate ${isLeader ? 'their Growth Plan' : planName} if it doesn't exist.`}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-6 py-4">
            {/* Strengths */}
            {idpTransferItems.strengths.length > 0 && (
              <div>
                <h4 className="font-medium text-green-700 flex items-center gap-2 mb-3">
                  <Sparkles className="h-4 w-4" />
                  Strengths ({idpTransferItems.strengths.filter(s => s.selected).length}/{idpTransferItems.strengths.length} selected)
                </h4>
                <div className="space-y-2">
                  {idpTransferItems.strengths.map((item, idx) => (
                    <label
                      key={idx}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        item.selected
                          ? 'bg-green-50 border-green-200'
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={item.selected}
                        onChange={() => toggleIDPTransferItem('strengths', idx)}
                        className="mt-1 h-4 w-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                      />
                      <span className="text-sm text-gray-700 flex-1">{item.text}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Development Areas */}
            {idpTransferItems.developmentAreas.length > 0 && (
              <div>
                <h4 className="font-medium text-amber-700 flex items-center gap-2 mb-3">
                  <Lightbulb className="h-4 w-4" />
                  Development Areas ({idpTransferItems.developmentAreas.filter(d => d.selected).length}/{idpTransferItems.developmentAreas.length} selected)
                </h4>
                <div className="space-y-2">
                  {idpTransferItems.developmentAreas.map((item, idx) => (
                    <label
                      key={idx}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        item.selected
                          ? 'bg-amber-50 border-amber-200'
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={item.selected}
                        onChange={() => toggleIDPTransferItem('developmentAreas', idx)}
                        className="mt-1 h-4 w-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                      />
                      <span className="text-sm text-gray-700 flex-1">{item.text}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Interventions */}
            {idpTransferItems.interventions.length > 0 && (
              <div>
                <h4 className="font-medium text-blue-700 flex items-center gap-2 mb-3">
                  <GraduationCap className="h-4 w-4" />
                  Interventions ({idpTransferItems.interventions.filter(i => i.selected).length}/{idpTransferItems.interventions.length} selected)
                </h4>
                <div className="space-y-2">
                  {idpTransferItems.interventions.map((item, idx) => (
                    <label
                      key={idx}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        item.selected
                          ? 'bg-blue-50 border-blue-200'
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={item.selected}
                        onChange={() => toggleIDPTransferItem('interventions', idx)}
                        className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 flex-1">{item.text}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Empty state */}
            {idpTransferItems.strengths.length === 0 &&
             idpTransferItems.developmentAreas.length === 0 &&
             idpTransferItems.interventions.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <BookOpen className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No discussion items available to transfer.</p>
                <p className="text-sm mt-1">Complete the KPA discussions first to populate this list.</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t pt-4 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {(() => {
                const total = idpTransferItems.strengths.filter(s => s.selected).length +
                              idpTransferItems.developmentAreas.filter(d => d.selected).length +
                              idpTransferItems.interventions.filter(i => i.selected).length
                return `${total} item${total !== 1 ? 's' : ''} selected for transfer`
              })()}
            </p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowIDPTransfer(false)} disabled={transferringToIDP}>
                Cancel
              </Button>
              <Button
                onClick={handleTransferToIDP}
                disabled={transferringToIDP || (
                  idpTransferItems.strengths.filter(s => s.selected).length === 0 &&
                  idpTransferItems.developmentAreas.filter(d => d.selected).length === 0 &&
                  idpTransferItems.interventions.filter(i => i.selected).length === 0
                )}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {transferringToIDP ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Transferring...
                  </>
                ) : (
                  <>
                    <ArrowRight className="h-4 w-4 mr-2" />
                    {memberIDP ? `Add to ${planName}` : `Initiate ${planName} & Add`}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
